import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import PageHeader from "../components/PageHeader";
import WinnerRank from "../components/WinnerRank";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getPsActiveSeason,
  getPsLeaderboard,
  getPsPool,
  getPsAllPlayers,
  getPsPayouts,
  getPsScoreboard,
  getPsSmackMessages,
  postPsSmackMessage,
  getPsSmackReplies,
  postPsSmackReply,
  reactPsSmack,
  deletePsSmackMessage,
} from "../api/mlbPlayoff";
import RulesPayoutsPanel from "../components/RulesPayoutsPanel";
import SmackBoard from "../components/SmackBoard";
import ScoreStrip from "../components/ScoreStrip";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";

const PS_RULES = [
  {
    heading: "Roster",
    lines: [
      "Pick six hitters from any clinched playoff team.",
      "No cap — pick any combination of players.",
      "Players are locked once their team plays its first playoff game.",
    ],
  },
  {
    heading: "Scoring",
    lines: [
      "Single=1",
      "Double=2",
      "Triple=3",
      "Home Run=4",
      "Walk / Out / Error / HBP = 0",
      "Most total bases across the entire playoffs wins.",
    ],
  },
  {
    heading: "Entries",
    lines: [
      "Multiple entries per user allowed.",
      "Each team name must be unique within the pool.",
    ],
  },
];

const MLB_ABBREVS = {
  "Arizona Diamondbacks": "ARI", "Atlanta Braves": "ATL", "Baltimore Orioles": "BAL",
  "Boston Red Sox": "BOS", "Chicago Cubs": "CHC", "Chicago White Sox": "CWS",
  "Cincinnati Reds": "CIN", "Cleveland Guardians": "CLE", "Colorado Rockies": "COL",
  "Detroit Tigers": "DET", "Houston Astros": "HOU", "Kansas City Royals": "KC",
  "Los Angeles Angels": "LAA", "Los Angeles Dodgers": "LAD", "Miami Marlins": "MIA",
  "Milwaukee Brewers": "MIL", "Minnesota Twins": "MIN", "New York Mets": "NYM",
  "New York Yankees": "NYY", "Oakland Athletics": "OAK", "Athletics": "ATH",
  "Sacramento Athletics": "ATH", "Philadelphia Phillies": "PHI", "Pittsburgh Pirates": "PIT",
  "San Diego Padres": "SD", "San Francisco Giants": "SF", "Seattle Mariners": "SEA",
  "St. Louis Cardinals": "STL", "Tampa Bay Rays": "TB", "Texas Rangers": "TEX",
  "Toronto Blue Jays": "TOR", "Washington Nationals": "WSH",
};

const MLB_TEAM_IDS = {
  ARI: 109, ATL: 144, BAL: 110, BOS: 111, CHC: 112, CWS: 145,
  CIN: 113, CLE: 114, COL: 115, DET: 116, HOU: 117, KC:  118,
  LAA: 108, LAD: 119, MIA: 146, MIL: 158, MIN: 142, NYM: 121,
  NYY: 147, ATH: 133, PHI: 143, PIT: 134, SD:  135, SF:  137,
  SEA: 136, STL: 138, TB:  139, TEX: 140, TOR: 141, WSH: 120,
};

function teamLogoUrl(teamName) {
  const id = MLB_TEAM_IDS[teamAbbr(teamName)];
  return id ? `https://www.mlbstatic.com/team-logos/${id}.svg` : null;
}

function playerHeadshotUrl(mlbId) {
  if (!mlbId) return null;
  return `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_68,q_auto:best/v1/people/${mlbId}/headshot/67/current`;
}

function teamAbbr(name) {
  if (!name) return "—";
  if (MLB_ABBREVS[name]) return MLB_ABBREVS[name];
  if (name.length <= 4) return name;
  return name.substring(0, 3).toUpperCase();
}

export default function MlbPlayoffActiveHome() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { poolId }       = useParams();
  const numericPoolId    = Number(poolId);

  const username = user?.username || "Player";

  const [season,      setSeason]      = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [scoreboard, setScoreboard] = useState(null);
  const [poolFinished, setPoolFinished] = useState(false);
  const [allPlayers,  setAllPlayers]  = useState([]);
  const [poolName,    setPoolName]    = useState("");
  const [poolDetail,  setPoolDetail]  = useState(null);
  const [poolPayouts, setPoolPayouts] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [pageError,   setPageError]   = useState("");
  const [lbSortField, setLbSortField] = useState("total_bases");
  const [lbSortDir,   setLbSortDir]   = useState("desc");

  const sortedLeaderboard = useMemo(() => {
    const arr = [...leaderboard];
    arr.sort((a, b) => {
      const av = a[lbSortField] ?? -1;
      const bv = b[lbSortField] ?? -1;
      return lbSortDir === "desc" ? bv - av : av - bv;
    });
    // `rank` here is the row's position under whatever sort is applied, so the
    // server's standing rank is kept alongside it — the trophy belongs to the
    // pool winner however the reader has chosen to sort.
    return arr.map((e, i) => ({ ...e, rank: i + 1, standing_rank: e.rank }));
  }, [leaderboard, lbSortField, lbSortDir]);

  function handleLbSort(field) {
    if (field === lbSortField) {
      setLbSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setLbSortField(field);
      setLbSortDir("desc");
    }
  }

  function SortTh({ field, children, ...props }) {
    const active = lbSortField === field;
    return (
      <Th
        cursor="pointer"
        userSelect="none"
        onClick={() => handleLbSort(field)}
        _hover={{ bg: active ? undefined : "blue.100" }}
        {...props}
      >
        {children}{active ? (lbSortDir === "desc" ? " ▼" : " ▲") : ""}
      </Th>
    );
  }

  const [selectedEntry, setSelectedEntry]   = useState(null);
  const [playerSearch,  setPlayerSearch]    = useState("");
  const [playerStatsPage, setPlayerStatsPage] = useState(1);

  const STATS_PAGE_SIZE = 50;

  const { isOpen: isEntryOpen, onOpen: onEntryOpen, onClose: onEntryClose } = useDisclosure();

  useEffect(() => {
    async function loadPage() {
      if (!numericPoolId || Number.isNaN(numericPoolId)) {
        setPageError("Invalid pool.");
        setLoadingPage(false);
        return;
      }
      try {
        setPageError("");
        const s = await getPsActiveSeason();
        setSeason(s);
        const [lbData, poolData, players, payouts] = await Promise.all([
          getPsLeaderboard(s.id, numericPoolId),
          getPsPool(s.id, numericPoolId),
          getPsAllPlayers(s.id),
          getPsPayouts(s.id, numericPoolId),
        ]);
        setLeaderboard(lbData.leaderboard || []);
        setPoolFinished(lbData.pool_finished === true);
        // Soft-fail: the strip is a nicety, and an empty one is better than a
        // page that will not load because the schedule call hiccuped.
        getPsScoreboard(s.id).then(setScoreboard).catch(() => setScoreboard(null));
        setPoolDetail(poolData);
        setPoolName(poolData.name || "");
        setAllPlayers(players);
        setPoolPayouts(payouts);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Unable to load page.";
        setPageError(typeof msg === "string" ? msg : "Unable to load page.");
      } finally {
        setLoadingPage(false);
      }
    }
    loadPage();
  }, [numericPoolId]);

  function isMyEntry(entry) {
    return (entry.username || "").trim().toLowerCase() === username.trim().toLowerCase();
  }

  const myPlayerIds = useMemo(() => {
    const ids = new Set();
    const lowerUser = username.trim().toLowerCase();
    for (const entry of leaderboard) {
      if ((entry.username || "").trim().toLowerCase() === lowerUser) {
        for (const p of entry.players || []) ids.add(p.player_id);
      }
    }
    return ids;
  }, [leaderboard, username]);

  const allPlayersRanked = useMemo(() => {
    const sorted = [...allPlayers].sort((a, b) => (b.total_bases ?? 0) - (a.total_bases ?? 0));
    let rank = 1;
    return sorted.map((p, i) => {
      const tb = p.total_bases ?? 0;
      if (i > 0 && tb < (sorted[i - 1].total_bases ?? 0)) rank = i + 1;
      return { ...p, tb_rank: rank };
    });
  }, [allPlayers]);

  const filteredPlayersRanked = useMemo(() => {
    if (!playerSearch) return allPlayersRanked;
    const q = playerSearch.toLowerCase();
    return allPlayersRanked.filter(
      (p) => p.name.toLowerCase().includes(q) || (p.team || "").toLowerCase().includes(q)
    );
  }, [allPlayersRanked, playerSearch]);

  const pagedPlayersRanked = useMemo(() => {
    const start = (playerStatsPage - 1) * STATS_PAGE_SIZE;
    return filteredPlayersRanked.slice(start, start + STATS_PAGE_SIZE);
  }, [filteredPlayersRanked, playerStatsPage]);

  const statsTotalPages = Math.max(1, Math.ceil(filteredPlayersRanked.length / STATS_PAGE_SIZE));

  useEffect(() => setPlayerStatsPage(1), [playerSearch]);

  // Unique picks: players on exactly one entry's roster
  const uniquePicks = useMemo(() => {
    const map = {};
    for (const entry of leaderboard) {
      for (const p of entry.players || []) {
        if (!map[p.player_id]) map[p.player_id] = { ...p, entry_names: [] };
        map[p.player_id].entry_names.push(entry.team_name);
      }
    }
    return Object.values(map)
      .filter((p) => p.entry_names.length === 1)
      .sort((a, b) => (b.total_bases ?? 0) - (a.total_bases ?? 0));
  }, [leaderboard]);

  function openEntryModal(entry) {
    setSelectedEntry(entry);
    onEntryOpen();
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  if (loadingPage) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading MLB Playoff Challenge...</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title="MLB PLAYOFF CHALLENGE"
        onBack={() => navigate("/mlb-playoff")}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={8}>
        {pageError && (
          <Alert status="error" borderRadius="md" mb={6}>
            <AlertIcon />{pageError}
          </Alert>
        )}

        <SmackBoard
          poolId={numericPoolId}
          seasonId={season?.id}
          apiFns={{
            getMessages: getPsSmackMessages,
            postMessage: postPsSmackMessage,
            getReplies: getPsSmackReplies,
            postReply: postPsSmackReply,
            react: reactPsSmack,
            deleteMessage: deletePsSmackMessage,
          }}
        />

        <ScoreStrip games={scoreboard?.games} />

        {/* Action buttons */}
        <HStack mb={6} spacing={4} flexWrap="wrap" align="center">
          <Button
            size="sm"
            bg="white"
            color={TOP_BAR_BLUE}
            onClick={() => navigate(`/mlb-playoff/current/${numericPoolId}/draft`)}
            _hover={{ bg: "gray.100" }}
          >
            Draft Team
          </Button>
          <Button
            size="sm"
            bg="white"
            color={TOP_BAR_BLUE}
            onClick={() => navigate(`/mlb-playoff/current/${numericPoolId}/rosters`)}
            _hover={{ bg: "gray.100" }}
          >
            Full Rosters View
          </Button>
          <Box flex="1" display={{ base: "none", md: "block" }} />
          {(poolDetail?.owner_user_id === user?.id || poolDetail?.current_user_is_co_admin) && (
            <Button
              size="sm"
              variant="outline"
              color="white"
              borderColor="whiteAlpha.500"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={() => navigate(`/mlb-playoff/setup/${numericPoolId}`)}
            >
              Pool Settings
            </Button>
          )}
        </HStack>

        {/* Leaderboard + Unique Picks */}
        <HStack align="stretch" spacing={6} flexWrap="wrap" mb={6}>

          {/* Leaderboard */}
          <Box
            flex="3"
            minW={{ base: "100%", md: "400px" }}
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={6}
            h={{ base: "auto", md: "470px" }}
            display="flex"
            flexDirection="column"
          >
            <Heading size="md" color={TOP_BAR_BLUE} mb={1} flexShrink={0}>
              {poolName ? `${poolName} Leaderboard` : "Leaderboard"}
            </Heading>
            <Text fontSize="xs" color="gray.500" mb={2} flexShrink={0}>
              Click team names to view rosters.
            </Text>
            <Box overflowY="auto" flex="1" overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} bg="white" zIndex={1}>
                  <Tr>
                    {/* w="1px" collapses a column to its own content under
                        auto table layout: a cell cannot render narrower than
                        its longest unbreakable word, so each of these lands on
                        its own header text. Whatever they give up goes to Team
                        Name and TB Leader, the only unconstrained columns.

                        None of them set nowrap, deliberately — the space
                        before the sort arrow then wraps and the arrow sits
                        under the word rather than beside it. */}
                    <Th w="1px" px={1}>#</Th>
                    <Th>Team Name</Th>
                    <SortTh
                      field="active_players"
                      w="1px"
                      px={1}
                      textAlign="center"
                      title="Players whose club is still in the postseason"
                    >
                      Active
                    </SortTh>
                    <SortTh field="total_bases" w="1px" px={1} textAlign="center" bg="blue.200" fontWeight="bold">TOTAL</SortTh>
                    <SortTh field="wc_bases" isNumeric w="35px" bg="blue.50" title="Wild Card" display={{ base: "none", md: "table-cell" }}>WC</SortTh>
                    <SortTh field="ds_bases" isNumeric w="35px" bg="blue.50" title="Division Series" display={{ base: "none", md: "table-cell" }}>DS</SortTh>
                    <SortTh field="cs_bases" isNumeric w="35px" bg="blue.50" title="Championship Series" display={{ base: "none", md: "table-cell" }}>CS</SortTh>
                    <SortTh field="ws_bases" isNumeric w="35px" bg="blue.50" title="World Series" display={{ base: "none", md: "table-cell" }}>WS</SortTh>
                    <Th bg="green.50">TB Leader</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedLeaderboard.length > 0 ? (
                    sortedLeaderboard.map((entry) => {
                      const isMe = isMyEntry(entry);
                      const fw   = isMe ? "bold" : "normal";
                      return (
                        <Tr key={entry.entry_id} bg={isMe ? "yellow.100" : "transparent"}>
                          <Td fontWeight={fw} px={1}>
                            <WinnerRank rank={entry.rank} finished={poolFinished}
                                        isWinner={entry.standing_rank === 1} />
                          </Td>
                          <Td fontWeight={fw} py={1}>
                            <Button
                              variant="link"
                              color={TOP_BAR_BLUE}
                              fontWeight={isMe ? "bold" : "medium"}
                              fontSize="sm"
                              textAlign="left"
                              whiteSpace="normal"
                              h="auto"
                              minH={0}
                              onClick={() => openEntryModal(entry)}
                            >
                              {entry.team_name}
                            </Button>
                          </Td>
                          <Td textAlign="center" px={1} fontWeight={fw}>
                            {entry.active_players == null ? (
                              "—"
                            ) : (
                              // Amber once a roster starts losing clubs, red
                              // when there is nothing left to score. Clicking
                              // the team name shows which players are out.
                              <Text
                                as="span"
                                fontWeight={entry.active_players === 0 ? "bold" : fw}
                                color={
                                  entry.active_players === 0
                                    ? "red.500"
                                    : entry.active_players < entry.total_players
                                    ? "orange.500"
                                    : "inherit"
                                }
                              >
                                {entry.active_players}
                              </Text>
                            )}
                          </Td>
                          <Td textAlign="center" fontWeight="bold" color={TOP_BAR_BLUE} style={{ background: isMe ? "#90cdf4" : "#bee3f8" }}>{entry.total_bases}</Td>
                          <Td isNumeric fontWeight={fw} color="gray.600" display={{ base: "none", md: "table-cell" }} style={{ background: isMe ? "#ebf8ff" : "#f0f8ff" }}>{entry.wc_bases || "—"}</Td>
                          <Td isNumeric fontWeight={fw} color="gray.600" display={{ base: "none", md: "table-cell" }} style={{ background: isMe ? "#ebf8ff" : "#f0f8ff" }}>{entry.ds_bases || "—"}</Td>
                          <Td isNumeric fontWeight={fw} color="gray.600" display={{ base: "none", md: "table-cell" }} style={{ background: isMe ? "#ebf8ff" : "#f0f8ff" }}>{entry.cs_bases || "—"}</Td>
                          <Td isNumeric fontWeight={fw} color="gray.600" display={{ base: "none", md: "table-cell" }} style={{ background: isMe ? "#ebf8ff" : "#f0f8ff" }}>{entry.ws_bases || "—"}</Td>
                          <Td fontWeight={fw} fontSize="sm" style={{ background: isMe ? "#f0fff4" : "#f6fff9" }}>
                            {(() => {
                              const players = entry.players || [];
                              if (!players.length) return "—";
                              const leader = players.reduce((best, p) =>
                                (p.total_bases ?? 0) > (best.total_bases ?? 0) ? p : best, players[0]
                              );
                              return leader.total_bases > 0
                                ? `${leader.name} (${leader.total_bases})`
                                : "—";
                            })()}
                          </Td>
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={9}><Text color="gray.500">No entries yet.</Text></Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>

          {/* Unique Picks */}
          <Box
            flex="2"
            minW={{ base: "100%", md: "220px" }}
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={6}
            h={{ base: "auto", md: "470px" }}
            display="flex"
            flexDirection="column"
          >
            <Heading size="md" color={TOP_BAR_BLUE} mb={1} flexShrink={0}>Unique Picks</Heading>
            <Text fontSize="xs" color="gray.500" mb={2} flexShrink={0}>
              Players on exactly one entry
            </Text>
            <Box overflowY="auto" flex="1">
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} bg="white" zIndex={1}>
                  <Tr>
                    <Th w="32px">#</Th>
                    <Th>Player</Th>
                    <Th isNumeric w="50px">TB</Th>
                    <Th>Entry</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {uniquePicks.length > 0 ? (
                    uniquePicks.map((p, i, arr) => {
                      const isMine = myPlayerIds.has(p.player_id);
                      const rank = i === 0 || (p.total_bases ?? 0) < (arr[i - 1].total_bases ?? 0)
                        ? i + 1
                        : arr.findIndex((x) => x.total_bases === p.total_bases) + 1;
                      return (
                        <Tr key={p.player_id} bg={isMine ? "yellow.100" : "transparent"}>
                          <Td fontWeight={isMine ? "bold" : "normal"}>{rank}</Td>
                          <Td py={1}>
                            <HStack spacing={2} align="center">
                              <Image
                                src={playerHeadshotUrl(p.mlb_id)}
                                boxSize="32px"
                                borderRadius="full"
                                objectFit="cover"
                                bg="gray.100"
                                flexShrink={0}
                              />
                              <Text fontSize="sm" fontWeight={isMine ? "bold" : "normal"}
                                textDecoration={p.is_eliminated ? "line-through" : "none"}
                                color={p.is_eliminated ? "gray.400" : "inherit"}>
                                {p.name}
                              </Text>
                            </HStack>
                          </Td>
                          <Td isNumeric fontWeight="bold">{p.total_bases ?? 0}</Td>
                          <Td fontWeight={isMine ? "bold" : "normal"} fontSize="sm">{p.entry_names[0]}</Td>
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={4}>
                        <Text color="gray.500">No unique picks yet.</Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </HStack>

        {/* Playoff Stats Table */}
        <Box
          bg="white"
          color="black"
          border={`3px solid ${TOP_BAR_BLUE}`}
          borderRadius="lg"
          boxShadow="lg"
          p={6}
        >
          <Flex align="center" justify="space-between" mb={3} flexWrap="wrap" gap={2}>
            <Heading size="md" color={TOP_BAR_BLUE}>MLB Playoff Stats — Ranked by Total Bases</Heading>
            <InputGroup size="sm" w="220px">
              <Input
                placeholder="Search player or team..."
                value={playerSearch}
                onChange={(e) => setPlayerSearch(e.target.value)}
                borderColor="gray.300"
                _focus={{ borderColor: TOP_BAR_BLUE }}
              />
              {playerSearch && (
                <InputRightElement>
                  <Button size="xs" variant="ghost" onClick={() => setPlayerSearch("")} color="gray.500">✕</Button>
                </InputRightElement>
              )}
            </InputGroup>
          </Flex>
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th w="36px" display={{ base: "none", md: "table-cell" }}>#</Th>
                  <Th w="160px">Player</Th>
                  <Th w="52px">Team</Th>
                  <Th w="44px" display={{ base: "none", md: "table-cell" }}>Pos</Th>
                  <Th isNumeric w="52px">TB</Th>
                  <Th isNumeric w="48px" display={{ base: "none", md: "table-cell" }}>HR</Th>
                  <Th isNumeric w="48px" display={{ base: "none", md: "table-cell" }}>3B</Th>
                  <Th isNumeric w="48px" display={{ base: "none", md: "table-cell" }}>2B</Th>
                  <Th isNumeric w="48px" display={{ base: "none", md: "table-cell" }}>1B</Th>
                  <Th isNumeric w="52px" display={{ base: "none", md: "table-cell" }}>H</Th>
                  <Th isNumeric w="52px" display={{ base: "none", md: "table-cell" }}>AB</Th>
                  <Th isNumeric w="48px" display={{ base: "none", md: "table-cell" }}>G</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pagedPlayersRanked.length > 0 ? (
                  pagedPlayersRanked.map((p) => (
                    <Tr key={p.player_id}>
                      <Td display={{ base: "none", md: "table-cell" }}>{p.tb_rank}</Td>
                      <Td w="160px" fontWeight="medium">{p.name}</Td>
                      <Td>
                        <HStack spacing={1} align="center">
                          {teamLogoUrl(p.team) && (
                            <Image src={teamLogoUrl(p.team)} boxSize="18px" objectFit="contain" flexShrink={0} />
                          )}
                          <Text fontWeight="semibold" fontSize="sm">{teamAbbr(p.team)}</Text>
                        </HStack>
                      </Td>
                      <Td display={{ base: "none", md: "table-cell" }} fontSize="xs" color="gray.500">{p.position || "—"}</Td>
                      <Td isNumeric fontWeight="bold" color={TOP_BAR_BLUE}>{p.total_bases ?? 0}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{p.home_runs ?? 0}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{p.triples ?? 0}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{p.doubles ?? 0}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{p.singles ?? 0}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{p.hits ?? 0}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{p.at_bats ?? 0}</Td>
                      <Td isNumeric display={{ base: "none", md: "table-cell" }}>{p.games ?? 0}</Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={12}><Text color="gray.500">No player data yet.</Text></Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
          {statsTotalPages > 1 && (
            <HStack justify="space-between" mt={3}>
              <Text fontSize="xs" color="gray.500">
                {filteredPlayersRanked.length} player{filteredPlayersRanked.length !== 1 ? "s" : ""}
              </Text>
              <HStack spacing={1}>
                <Button size="xs" variant="ghost" isDisabled={playerStatsPage === 1} onClick={() => setPlayerStatsPage(1)}>«</Button>
                <Button size="xs" variant="ghost" isDisabled={playerStatsPage === 1} onClick={() => setPlayerStatsPage((p) => p - 1)}>‹</Button>
                <Text fontSize="xs" color="gray.600" px={1}>{playerStatsPage} / {statsTotalPages}</Text>
                <Button size="xs" variant="ghost" isDisabled={playerStatsPage === statsTotalPages} onClick={() => setPlayerStatsPage((p) => p + 1)}>›</Button>
                <Button size="xs" variant="ghost" isDisabled={playerStatsPage === statsTotalPages} onClick={() => setPlayerStatsPage(statsTotalPages)}>»</Button>
              </HStack>
            </HStack>
          )}
        </Box>
      </Container>

      {/* Entry roster modal */}
      {selectedEntry && (
        <Modal isOpen={isEntryOpen} onClose={onEntryClose} isCentered size="lg" motionPreset="scale">
          <ModalOverlay bg="blackAlpha.700" />
          <ModalContent bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg">
            <ModalHeader color={TOP_BAR_BLUE} pr={10}>{selectedEntry.team_name}</ModalHeader>
            <ModalCloseButton color={TOP_BAR_BLUE} />
            <ModalBody pb={6}>
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Player</Th>
                      <Th textAlign="center" bg="blue.200" fontWeight="bold">TB</Th>
                      <Th isNumeric bg="blue.50" title="Wild Card">WC</Th>
                      <Th isNumeric bg="blue.50" title="Division Series">DS</Th>
                      <Th isNumeric bg="blue.50" title="Championship Series">CS</Th>
                      <Th isNumeric bg="blue.50" title="World Series">WS</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(selectedEntry.players || []).map((p) => (
                      <Tr key={p.player_id} opacity={p.is_eliminated ? 0.5 : 1}>
                        <Td py={2}>
                          <HStack spacing={2} align="center">
                            <Image
                              src={playerHeadshotUrl(p.mlb_id)}
                              boxSize="36px"
                              borderRadius="full"
                              objectFit="cover"
                              bg="gray.100"
                              flexShrink={0}
                            />
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              textDecoration={p.is_eliminated ? "line-through" : "none"}
                              color={p.is_eliminated ? "gray.400" : "inherit"}
                            >
                              {p.name}
                            </Text>
                          </HStack>
                        </Td>
                        <Td textAlign="center" fontWeight="bold" color={TOP_BAR_BLUE} bg="blue.200">{p.total_bases}</Td>
                        <Td isNumeric color="gray.600" bg="blue.50">{p.wc_bases || "—"}</Td>
                        <Td isNumeric color="gray.600" bg="blue.50">{p.ds_bases || "—"}</Td>
                        <Td isNumeric color="gray.600" bg="blue.50">{p.cs_bases || "—"}</Td>
                        <Td isNumeric color="gray.600" bg="blue.50">{p.ws_bases || "—"}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                  <Tfoot>
                    <Tr bg="gray.50">
                      <Td fontWeight="bold">Total</Td>
                      <Td textAlign="center" fontWeight="bold" color={TOP_BAR_BLUE} bg="blue.200">{selectedEntry.total_bases}</Td>
                      <Td isNumeric fontWeight="bold" bg="blue.50">{selectedEntry.wc_bases || "—"}</Td>
                      <Td isNumeric fontWeight="bold" bg="blue.50">{selectedEntry.ds_bases || "—"}</Td>
                      <Td isNumeric fontWeight="bold" bg="blue.50">{selectedEntry.cs_bases || "—"}</Td>
                      <Td isNumeric fontWeight="bold" bg="blue.50">{selectedEntry.ws_bases || "—"}</Td>
                    </Tr>
                  </Tfoot>
                </Table>
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      <RulesPayoutsPanel
        rules={PS_RULES}
        payouts={poolPayouts}
        totalPot={poolDetail?.total_pot}
      />
    </Box>
  );
}
